import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { getCostRates, setCostRates } from '../../api/client'
import { useCostLock } from './costLock'
import CostUnlock from './CostUnlock'

/**
 * The two hourly rates per person, plus the per-project exceptions.
 *
 * Blank is not zero. Clearing a field means "nobody has priced this person",
 * and the reports list them separately rather than costing their hours at
 * nothing -- a silent zero understates the company cost with nothing on screen
 * to say so.
 *
 * Cost/rate/type are edited right in the row -- the same commit-on-blur cell
 * the Dotacion tab uses for hours, just money instead. Only the per-project
 * exceptions still open a dialog: a variable-length list doesn't fit in one
 * cell the way a single number or a two-way pick does.
 */
export default function CostRates() {
  const { t } = useTranslation(['clevel', 'common'])
  const { password, unlocked, lock } = useCostLock()
  const [data, setData] = useState(null)
  const [editing, setEditing] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(null)   // `${memberId}:${field}` while a cell's write is in flight

  const k = useCallback((suffix, opts) => t(`capacity.rates.${suffix}`, opts), [t])

  // No synchronous setState on the way in: when there is no password the
  // component returns the locked panel before it ever reads `data`.
  const load = useCallback(() => {
    if (!password) return
    getCostRates(password)
      .then(d => { setData(d); setError('') })
      .catch(err => setError(err.response?.data?.detail || k('loadFailed')))
  }, [password, k])

  useEffect(() => {
    if (!password) return undefined
    let cancelled = false
    getCostRates(password)
      .then(d => { if (!cancelled) { setData(d); setError('') } })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.detail || k('loadFailed'))
      })
    return () => { cancelled = true }
  }, [password, k])

  const saveField = async (member, field, value) => {
    const key = `${member.team_member_id}:${field}`
    setSaving(key)
    setError('')
    try {
      await setCostRates(member.team_member_id, { [field]: value }, password)
      load()
    } catch (err) {
      setError(err.response?.data?.detail || k('saveFailed'))
    } finally {
      setSaving(null)
    }
  }

  if (!unlocked) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-12 text-center space-y-3">
        <p className="text-sm text-gray-500">{k('locked')}</p>
        <UnlockButton label={k('unlock')} />
      </div>
    )
  }

  if (error) return <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>
  if (!data) return <p className="text-sm text-gray-400 px-1">{t('capacity.loading')}</p>

  return (
    <div className="space-y-4">
      {editing && (
        <OverridesModal member={editing} projects={data.projects} password={password}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load() }} />
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{k('title')}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{k('subtitle')}</p>
        </div>
        <button onClick={lock}
          className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
          🔓 {k('lock')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2 font-semibold text-gray-600">{k('person')}</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">{k('role')}</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">{k('costType')}</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">{k('company')}</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">{k('client')}</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">{k('overrides')}</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m, i) => (
              <tr key={m.team_member_id} className={i % 2 ? 'bg-gray-50/60' : ''}>
                <td className="px-4 py-1.5 text-gray-800">
                  {m.name}
                  {!m.is_active && (
                    <span className="ml-2 text-[10px] text-gray-400">{k('inactive')}</span>
                  )}
                </td>
                <td className="px-4 py-1.5 text-gray-400">{m.role || '—'}</td>
                <td className="px-4 py-1.5">
                  <select value={m.cost_type ?? ''}
                    disabled={saving === `${m.team_member_id}:cost_type`}
                    onChange={e => saveField(m, 'cost_type', e.target.value === '' ? null : e.target.value)}
                    className={`bg-transparent text-xs rounded border border-transparent
                      hover:border-gray-200 focus:border-gray-400 focus:bg-white focus:outline-none
                      px-1 py-0.5 -mx-1 disabled:opacity-50
                      ${m.cost_type ? 'text-gray-700' : 'text-amber-600'}`}>
                    <option value="">{k('unset')}</option>
                    <option value="gasto">{k('costTypeOptions.gasto')}</option>
                    <option value="costo">{k('costTypeOptions.costo')}</option>
                  </select>
                </td>
                <td className="px-4 py-1.5 text-right">
                  <MoneyCell value={m.cost_company_clp} placeholder={k('unset')}
                    disabled={saving === `${m.team_member_id}:cost_company_clp`}
                    onCommit={v => saveField(m, 'cost_company_clp', v)} />
                </td>
                <td className="px-4 py-1.5 text-right">
                  <MoneyCell value={m.cost_client_usd} placeholder={k('unset')}
                    disabled={saving === `${m.team_member_id}:cost_client_usd`}
                    onCommit={v => saveField(m, 'cost_client_usd', v)} />
                </td>
                <td className="px-4 py-1.5 text-gray-500">
                  <button onClick={() => setEditing(m)} className="hover:text-gray-800">
                    {m.overrides.length === 0 ? k('addOverride') : k('overrideCount', { count: m.overrides.length })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400">{k('blankHint')}</p>
    </div>
  )
}

// A commit-on-blur money field: blank means "unset" (never zero), only fires
// onCommit when the value actually changed, Escape reverts the draft. Same
// idiom as StaffingTab's HoursCell, minus its 0-fallback and upper cap --
// neither makes sense for a rate that can be genuinely unset or unbounded.
function MoneyCell({ value, placeholder, disabled, onCommit }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value))
  const [seen, setSeen] = useState(value)
  if (value !== seen) {
    setSeen(value)
    setDraft(value == null ? '' : String(value))
  }

  const commit = () => {
    const trimmed = draft.trim()
    if (trimmed === '') {
      if (value != null) onCommit(null)
      return
    }
    const next = Number(trimmed)
    if (!Number.isFinite(next) || next < 0) {
      setDraft(value == null ? '' : String(value))
      return
    }
    if (next !== value) onCommit(next)
  }

  if (disabled) {
    return <span className="tabular-nums text-gray-400">{draft || '…'}</span>
  }
  return (
    <input
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') setDraft(value == null ? '' : String(value))
      }}
      placeholder={placeholder}
      className={`w-24 bg-transparent text-right tabular-nums text-xs rounded
        border border-transparent hover:border-gray-200 focus:border-gray-400
        focus:bg-white focus:outline-none px-1 py-0.5
        ${value == null ? 'text-amber-600' : 'text-gray-700'}`}
    />
  )
}

function UnlockButton({ label }) {
  const [asking, setAsking] = useState(false)
  return (
    <>
      {asking && <CostUnlock onClose={() => setAsking(false)} />}
      <button onClick={() => setAsking(true)}
        className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800">
        🔒 {label}
      </button>
    </>
  )
}

function OverridesModal({ member, projects, password, onClose, onSaved }) {
  const { t } = useTranslation(['clevel', 'common'])
  const k = (suffix, opts) => t(`capacity.rates.${suffix}`, opts)
  const [overrides, setOverrides] = useState(
    member.overrides.map(o => ({ ...o, rate_usd: String(o.rate_usd) })))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      await setCostRates(member.team_member_id, {
        overrides: overrides.map(o => ({
          project_id: o.project_id,
          rate_usd: o.rate_usd === '' ? null : Number(o.rate_usd),
        })),
      }, password)
      onSaved()
    } catch (err) {
      setError(err.response?.data?.detail || k('saveFailed'))
    } finally {
      setBusy(false)
    }
  }

  const addOverride = () => {
    const taken = new Set(overrides.map(o => o.project_id))
    const next = projects.find(p => !taken.has(p.project_id))
    if (next) setOverrides(prev => [...prev, { project_id: next.project_id, rate_usd: '' }])
  }

  return (
    <Modal title={k('overridesModalTitle', { name: member.name })} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(480px, 85vw)' }}>
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600">{k('overridesTitle')}</p>
            <button onClick={addOverride}
              className="text-xs text-gray-500 hover:text-gray-800">+ {k('addOverride')}</button>
          </div>
          <p className="text-[11px] text-gray-400">{k('overridesHint')}</p>
          {overrides.map((o, i) => (
            <div key={i} className="flex gap-2">
              <select value={o.project_id}
                onChange={e => setOverrides(prev => prev.map((x, j) =>
                  j === i ? { ...x, project_id: Number(e.target.value) } : x))}
                className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.code ? `${p.code} · ${p.name}` : p.name}
                  </option>
                ))}
              </select>
              <input type="number" min="0" step="0.5" value={o.rate_usd}
                onChange={e => setOverrides(prev => prev.map((x, j) =>
                  j === i ? { ...x, rate_usd: e.target.value } : x))}
                className="w-32 border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
              <button
                onClick={() => setOverrides(prev => prev.map((x, j) =>
                  j === i ? { ...x, rate_usd: '' } : x))}
                title={k('clearOverride')}
                className="px-2 text-gray-300 hover:text-red-500">×</button>
            </div>
          ))}
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={save} disabled={busy}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
            {busy ? t('common:actions.saving', '…') : t('common:actions.save')}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200">
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
